import {
  APIComponentAttributes,
  APIComponentType,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
} from '@ardoq/api-types';
import { ModelTypes } from '@ardoq/data-model';

export type ComponentTypeByComponentId = Record<
  ArdoqId,
  APIComponentType | null
>;

export type ReferenceTypeByReferenceId = Record<
  ArdoqId,
  APIReferenceType | null
>;

export type ReferenceFilterContext = {
  componentTypeByComponentId: ComponentTypeByComponentId;
  referenceTypeByReferenceId: ReferenceTypeByReferenceId;
};

export const buildComponentTypeByComponentId = (
  components: APIComponentAttributes[],
  typesByModelId: Record<ArdoqId, ModelTypes>
): ComponentTypeByComponentId => {
  const out: ComponentTypeByComponentId = {};
  for (const component of components) {
    out[component._id] =
      typesByModelId[component.model]?.componentTypesById[component.typeId] ??
      null;
  }
  return out;
};

export const buildReferenceTypeByReferenceId = (
  references: APIReferenceAttributes[],
  typesByModelId: Record<ArdoqId, ModelTypes>
): ReferenceTypeByReferenceId => {
  const out: ReferenceTypeByReferenceId = {};
  for (const reference of references) {
    out[reference._id] =
      typesByModelId[reference.model]?.referenceTypesById?.[reference.type] ??
      null;
  }
  return out;
};

type BuildReferenceFilterContextArgs = {
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
  typesByModelId: Record<ArdoqId, ModelTypes>;
};

export const buildReferenceFilterContext = ({
  components,
  references,
  typesByModelId,
}: BuildReferenceFilterContextArgs): ReferenceFilterContext => ({
  componentTypeByComponentId: buildComponentTypeByComponentId(
    components,
    typesByModelId
  ),
  referenceTypeByReferenceId: buildReferenceTypeByReferenceId(
    references,
    typesByModelId
  ),
});

/**
 * Placeholder ctx for call sites that thread ReferenceFilterContext but
 * don't have scope data on hand. ctx-aware inclusion checks fall back to
 * the slow per-id model lookups when an entry is missing — so an empty
 * ctx is semantically equivalent to the pre-ctx slow path.
 */
export const emptyReferenceFilterContext = (): ReferenceFilterContext => ({
  componentTypeByComponentId: {},
  referenceTypeByReferenceId: {},
});
